// EditableText — writing on the paper (owner, 29 Oct): one text field of a
// material, written where it is read.
//
// A small leaf that renders a text value as its reading face until the author
// activates it (a click puts the caret where it was clicked; Enter on the
// focused face puts it at the end), then swaps to a textarea that is the
// reading face exactly: same font, size, line height, colour and width, no
// border, background, padding or shadow, so nothing on the page moves. The
// change is kept when the author leaves (a click elsewhere, Enter at the end,
// or Esc); there is no Save or Cancel: undo in the top bar covers mistakes.
// While the author writes, each change also goes up as a draft (`onDraft`),
// which the host saves once the author pauses or the page is closed.
// It is the MANUAL twin of an agent edit: the value it commits flows through
// the SAME `update_element_text` primitive (the host wires `onCommit` to its
// `updateElementText` port), never a bespoke UI-only mutation.
//
// It is the one Editor leaf that holds EPHEMERAL local state — the in-progress
// input buffer (`draft`), whether it is editing, and where the caret goes —
// exactly as the design brief sanctions (like a composer's own text buffer).
// That state never escapes: the only thing that leaves is the committed
// string, and only when it differs from the incoming `value`. No I/O, no
// fetch, no domain knowledge; the display always reads the CURRENT `value`
// prop, so an external change (a successful commit, or a rollback) reflects
// immediately.
//
// Full-width box, both faces: display and edit share one block, `width:100%`
// box (see .css), so a long field WRAPS within its column. Edit mode is an
// auto-growing `<textarea>` (`field-sizing:content`): it grows as you type
// and the page scrolls; nothing above it moves. A caller whose reading face
// has its own type (a paragraph's reading face, Prose) passes that class as
// `className`, so both faces take it.
//
// A11y / design-system discipline:
//   • Keyboard-operable: the resting face is a native <button> (Enter/Space
//     to write, for free); the textarea takes Enter (keep) / Escape (keep and
//     leave), Shift+Enter inserts a newline.
//   • Named: `ariaLabel` names both the face and the textarea (a control is
//     never nameless) — and it carries the "editable" meaning for AT.
//   • Focus without a caret (WCAG 2.4.7): a thin `fg-dim` line in the left
//     margin, outside the text, so the text never moves.
//   • Text is text: a paste lands as plain text (a textarea holds no markup).

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
    matchRule,
    nextSlashTrigger,
    type Rule,
    type RuleMatch,
    type SlashTriggerState,
    slashBeforeOf,
    slashQueryOf,
} from "../inputRules.js";
import "./EditableText.css";

export type EditableTextProps = {
    readonly value: string;
    // Commit the new text UP. Called on blur / Enter / Esc when it CHANGED,
    // or (with `onDraft`) whenever a draft went up, so the host can send it.
    readonly onCommit: (text: string) => void;
    // The text while it is still being typed, on every change (the host saves
    // it once the author pauses). Not reported while a "/" menu is open or a
    // markdown rule is being typed (`#`, `-`, `1.`), whose text is a command.
    // Absent: only `onCommit` reports.
    readonly onDraft?: (text: string) => void;
    // Names the affordance + textarea for AT, and carries the "editable" meaning.
    readonly ariaLabel: string;
    // Shown (dimmed) in the resting affordance when `value` is empty, and as the
    // textarea's placeholder while editing. Lets an empty optional field (e.g. an
    // unauthored MCQ feedback) still present a clickable "Add …" target instead
    // of an invisible empty box. Absent ⇒ an empty value renders as an empty box.
    readonly placeholder?: string;
    // Optional custom RESTING face. When present, the read-only affordance renders
    // `renderDisplay(value)` instead of the raw string — so a field whose source
    // is markdown (a prose block) keeps its parsed reading face at rest while
    // still editing the raw source in the textarea. The EDIT face is always the
    // plain textarea over `value` (you edit the source, not the rendered node).
    // Absent ⇒ the resting face is the plain string (the default for plain-text
    // fields: heading, objective, choice text, feedback).
    readonly renderDisplay?: (value: string) => ReactNode;
    readonly className?: string;
    // The markdown input-rule table to detect against WHILE typing (prose
    // blocks only — `# `/`## `/`### ` etc, inputRules.ts's MARKDOWN_RULES).
    // Absent ⇒ no live detection (the default for every non-prose field:
    // heading text, objective text, an MCQ stem/choice, feedback — none of
    // which are markdown-interpreted). This leaf runs the PURE `matchRule`
    // engine and reports the RAW match up; it never builds the resulting
    // element itself (`onRuleMatch`'s caller has the typed Element `matchRule`
    // needs `RuleContext` for — see ProseRenderer).
    readonly rules?: readonly Rule[];
    // A rule matched the draft WHILE typing. Absent ⇒ `rules` (if given) is
    // still detected against but never reported — effectively no-detection.
    readonly onRuleMatch?: (match: RuleMatch) => void;
    // The `/` slash-trigger's CURRENT state, CONTROLLED by the caller (the
    // same discipline as MaterialDocument's `slashMenu` prop) — this leaf
    // holds none of its own slash state. Absent ⇒ treated as "closed".
    readonly slashTriggerState?: SlashTriggerState;
    // Report the NEXT slash-trigger state after a keystroke, plus the
    // query typed after the leading `/` (empty string once the state is
    // anything but "open"/a bare "/"). Absent ⇒ no slash-trigger detection.
    readonly onSlashTrigger?: (state: SlashTriggerState, query: string) => void;
    // Move the OPEN slash menu's keyboard cursor (↑/↓) — reported UP because
    // this leaf holds no menu state; the host owns the active index and the
    // filtered rows (same discipline as `slashTriggerState`). Fired only while
    // `slashTriggerState === "open"`. Absent ⇒ arrows do native caret movement.
    readonly onSlashNavigate?: (direction: "up" | "down") => void;
    // Commit the OPEN slash menu's ACTIVE row (Enter) — the keyboard twin of the
    // menu's mouse click. The host picks `items[activeIndex]` and inserts. Fired
    // only while the menu is open; when it picks (true), Enter never commits
    // text or splits a block. Nothing to pick (false: no rows, or offline):
    // Enter goes on as if no menu were open. Absent ⇒ Enter behaves normally.
    readonly onSlashCommitActive?: () => boolean;
    // The document-first "new block below" gesture: Enter pressed with the caret
    // at the END of the text and no open slash menu. Carries the just-typed text
    // so the host persists THIS block's edit and the sibling insert as ONE
    // sequenced chain (threading the returned content hash — no optimistic-lock
    // race between two independent edits). Wired only on top-level document
    // blocks (prose / heading / objective); absent on intra-MCQ fields, where
    // Enter just commits. Mid-text Enter (caret not at end) still commits — a
    // true split is deferred (document-first-editor.md §4).
    readonly onEnterAtEnd?: (committedText: string) => void;
    // The document-first "delete this empty block, focus the previous" gesture:
    // Backspace pressed while the text is EMPTY. Wired only on top-level document
    // blocks; absent elsewhere. A non-empty block's Backspace edits text as
    // normal (a text-merge into the previous block is deferred, §4).
    readonly onBackspaceEmpty?: () => void;
    // Enter EDITING mode (with focus) the moment THIS instance mounts,
    // instead of the resting display face — the block-editor "keep typing
    // after `# `, after a slash-insert" continuity fix (Bug 1, 2026-08-19).
    // A retype/insert always mounts a BRAND-NEW EditableText (a different
    // renderer takes over the same element id, or the element is new
    // outright) — there is no PREVIOUS instance whose `editing`/`draft`
    // state could carry over, so this seeds the new one's INITIAL state
    // instead of trying to preserve anything across the swap. Read ONLY at
    // mount (`useState`'s initializer runs once); a later prop flip does
    // NOT retroactively re-open editing — see `onAutoEditConsumed`.
    readonly autoEdit?: boolean;
    // Fired once, right after mount, when `autoEdit` was true — lets the
    // host clear its one-shot signal so a LATER remount of this same
    // element (a second retype) needs an explicit new signal rather than
    // spuriously re-firing off a stale one.
    readonly onAutoEditConsumed?: () => void;
    // The resting face's click goes UP instead of opening the field here:
    // a block that does not exist yet (a blank page's first paragraph),
    // which the host makes, and whose own new field then takes the caret.
    readonly onActivate?: () => void;
    // The field's text ceiling (a block's `{block}`, a purpose's CL-6):
    // typing stops there, and near it the Limit hint shows under the field.
    // Absent: no ceiling here.
    readonly maxLength?: number;
    // A paste that would go past the ceiling goes UP as one text instead
    // (the host takes it in through the file door, as a source); nothing
    // lands in the field. Absent: the field keeps what fits.
    readonly onPasteTooLong?: (text: string) => void;
};

// The Limit hint shows from here on (the share of the ceiling used).
const NEAR_CEILING = 0.9;

// Where a click at (x, y) on the resting face lands in `value`: the text
// offset under the pointer, counted through the face's text. Exact when the
// face shows the text as written (a plain field or paragraph); close when it
// renders markup (bold, a list), and never past the end. Reads the DOM at
// the click (the event's own moment), nothing else.
const caretOffsetAt = (
    face: HTMLElement,
    x: number,
    y: number,
    value: string,
): number => {
    const at = pointToText(x, y);
    if (at === undefined) return value.length;
    const texts = textNodes(face);
    const i = texts.indexOf(at.node);
    // Not on the face's text (its padding, an icon): the end.
    if (i < 0) return value.length;
    const before = texts
        .slice(0, i)
        .reduce((sum, n) => sum + (n.textContent ?? "").length, 0);
    return Math.min(before + at.offset, value.length);
};

const textNodes = (root: Node): readonly Node[] => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const out: Node[] = [];
    for (let n = walker.nextNode(); n !== null; n = walker.nextNode())
        out.push(n);
    return out;
};

const pointToText = (
    x: number,
    y: number,
): { readonly node: Node; readonly offset: number } | undefined => {
    if (typeof document.caretPositionFromPoint === "function") {
        const p = document.caretPositionFromPoint(x, y);
        return p === null
            ? undefined
            : { node: p.offsetNode, offset: p.offset };
    }
    if (typeof document.caretRangeFromPoint !== "function") return undefined;
    const r = document.caretRangeFromPoint(x, y);
    return r === null
        ? undefined
        : { node: r.startContainer, offset: r.startOffset };
};

export const EditableText = ({
    value,
    onCommit,
    onDraft,
    ariaLabel,
    placeholder,
    renderDisplay,
    className,
    rules,
    onRuleMatch,
    slashTriggerState,
    onSlashTrigger,
    onSlashNavigate,
    onSlashCommitActive,
    onEnterAtEnd,
    onBackspaceEmpty,
    autoEdit,
    onAutoEditConsumed,
    onActivate,
    maxLength,
    onPasteTooLong,
}: EditableTextProps) => {
    const { t } = useLingui();
    const [editing, setEditing] = useState(autoEdit === true);
    const [draft, setDraft] = useState(value);
    // Guards a double-commit: pressing Enter commits AND unmounts the textarea,
    // whose blur would otherwise commit a second time before `value` catches up.
    const doneRef = useRef(false);
    // A draft went up since the field opened: leaving it commits even when
    // the text is back where it started (the host holds the draft).
    const draftedRef = useRef(false);
    // Where the caret goes when the field opens (absent: the end).
    const caretRef = useRef<number | null>(null);
    const fieldRef = useRef<HTMLTextAreaElement>(null);

    // The field takes focus and the caret where the author clicked, before
    // paint (so the caret never flashes at the start).
    useLayoutEffect(() => {
        const field = fieldRef.current;
        if (!editing || field === null) return;
        const at = caretRef.current ?? field.value.length;
        caretRef.current = null;
        field.focus({ preventScroll: true });
        field.setSelectionRange(at, at);
    }, [editing]);

    // Consume the mount-time `autoEdit` signal exactly once. Intentionally
    // depends on nothing (mirrors the `useState(autoEdit)` initializer
    // above, which also reads `autoEdit` ONLY at mount) — a prop change on
    // an already-mounted instance must NOT re-fire this; only a fresh mount
    // (a new `autoEdit={true}`) does, and that is a new effect instance by
    // construction.
    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once by design — autoEdit is read at mount only, exactly like the useState initializer above.
    useEffect(() => {
        if (autoEdit === true) onAutoEditConsumed?.();
    }, []);

    // A "/" row was picked (by the pointer too, which never reaches this
    // field's keys): the "/query" was a command, so drop it and stop
    // editing, as Enter on the menu does. Text before the "/" stays (pen Q73:
    // the line splits at the "/").
    const keepBeforeSlash = (text: string) => {
        doneRef.current = true;
        setEditing(false);
        const before = slashBeforeOf(text);
        if (before !== "" && before !== value) onCommit(before);
    };
    // biome-ignore lint/correctness/useExhaustiveDependencies: fires on the pick only; the draft is read as it stands then.
    useEffect(() => {
        if (slashTriggerState !== "picked" || doneRef.current) return;
        keepBeforeSlash(draft);
    }, [slashTriggerState]);

    const enter = (at: number) => {
        doneRef.current = false;
        draftedRef.current = false;
        caretRef.current = at;
        setDraft(value);
        setEditing(true);
    };
    const commit = () => {
        if (doneRef.current) return;
        doneRef.current = true;
        setEditing(false);
        // Leaving the field closes its "/" menu: what was typed is text.
        if (slashTriggerState !== undefined && slashTriggerState !== "closed")
            onSlashTrigger?.("closed", "");
        if (draft !== value || draftedRef.current) onCommit(draft);
    };
    // Report the draft up, unless it is a command being typed: a "/" menu
    // that opens with it, or a markdown rule that fires with it or with the
    // next space.
    const reportDraft = (text: string, slash: SlashTriggerState) => {
        if (onDraft === undefined || slash === "open") return;
        if (
            rules !== undefined &&
            (matchRule(rules, text) !== undefined ||
                matchRule(rules, `${text} `) !== undefined)
        )
            return;
        draftedRef.current = true;
        onDraft(text);
    };

    const cls = `bilda-editable-text${className ? ` ${className}` : ""}`;

    // The query typed after the "/" (inputRules.ts: at the start of the line
    // or after a space, pen Q73).
    const slashQuery = slashQueryOf;

    const nearCeiling =
        maxLength !== undefined && draft.length >= maxLength * NEAR_CEILING;

    if (editing)
        return (
            <>
                <textarea
                    // Focus follows the swap (the layout effect above): the
                    // field REPLACES the face the author just activated.
                    ref={fieldRef}
                    rows={1}
                    className={`${cls} bilda-editable-text--input`}
                    aria-label={ariaLabel}
                    placeholder={placeholder}
                    maxLength={maxLength}
                    value={draft}
                    onPaste={(event) => {
                        if (
                            maxLength === undefined ||
                            onPasteTooLong === undefined
                        )
                            return;
                        // A textarea takes only text; a paste past the ceiling
                        // goes up whole instead of being cut where it fills.
                        const text = event.clipboardData.getData("text/plain");
                        const field = event.currentTarget;
                        const kept =
                            draft.length -
                            (field.selectionEnd - field.selectionStart);
                        if (kept + text.length <= maxLength) return;
                        event.preventDefault();
                        onPasteTooLong(text);
                    }}
                    onChange={(event) => {
                        const next = event.target.value;
                        setDraft(next);
                        // Live "as you type" detection — SHORTCUT (lifecycle-design:
                        // stated explicitly): only checked while the caret sits at
                        // the END of the text, the normal forward-typing case. A
                        // caret placed mid-text (a paste, an edit further back)
                        // never fires a rule/slash-trigger — this avoids retyping
                        // or opening the menu off a prefix that merely appears
                        // earlier in already-typed content.
                        const caretAtEnd =
                            event.target.selectionStart === next.length;
                        if (caretAtEnd && rules !== undefined) {
                            const match = matchRule(rules, next);
                            if (match !== undefined) onRuleMatch?.(match);
                        }
                        const slash =
                            caretAtEnd && onSlashTrigger !== undefined
                                ? nextSlashTrigger(
                                      slashTriggerState ?? "closed",
                                      next,
                                  )
                                : (slashTriggerState ?? "closed");
                        if (caretAtEnd && onSlashTrigger !== undefined)
                            onSlashTrigger(slash, slashQuery(next));
                        reportDraft(next, slash);
                    }}
                    onBlur={commit}
                    onKeyDown={(event) => {
                        const field = event.currentTarget;
                        const slashOpen = slashTriggerState === "open";
                        // --- Slash-menu keyboard nav (only while the menu is open) ---
                        // ↑/↓ move the menu cursor; the caret does not move. When the
                        // menu is closed these fall through to the textarea's native
                        // line movement (no preventDefault).
                        if (
                            slashOpen &&
                            (event.key === "ArrowDown" ||
                                event.key === "ArrowUp") &&
                            onSlashNavigate !== undefined
                        ) {
                            event.preventDefault();
                            onSlashNavigate(
                                event.key === "ArrowDown" ? "down" : "up",
                            );
                            return;
                        }
                        // Enter: pick the slash row if the menu is open; else the
                        // document-first "new block below" at end-of-text; else the
                        // plain text commit. Shift+Enter is always a newline (real
                        // breaks in a wrapping objective / paragraph).
                        if (event.key === "Enter" && !event.shiftKey) {
                            event.preventDefault();
                            // Already left (a repeated Enter before the
                            // field went): nothing more.
                            if (doneRef.current) return;
                            if (slashOpen && onSlashCommitActive?.() === true) {
                                // The "/query" was a command trigger, not content:
                                // drop it (guard the ensuing blur so it can't
                                // commit "/" as this block's text) as the host
                                // acts on the picked row; text before it stays.
                                keepBeforeSlash(draft);
                                return;
                            }
                            // Nothing picked: the menu goes, the text stays.
                            if (slashOpen) onSlashTrigger?.("closed", "");
                            const caretAtEnd =
                                field.selectionStart === draft.length &&
                                field.selectionEnd === draft.length;
                            if (onEnterAtEnd !== undefined && caretAtEnd) {
                                // Hand the text to the host, which persists this
                                // edit AND the sibling insert in one sequenced
                                // chain. Guard the pending blur so it does not
                                // double-commit the same text behind the host's back.
                                doneRef.current = true;
                                setEditing(false);
                                onEnterAtEnd(draft);
                                return;
                            }
                            commit();
                            return;
                        }
                        if (event.key === "Escape") {
                            event.preventDefault();
                            // An OPEN slash menu takes the first Escape: it
                            // closes and what was typed stays as plain text,
                            // the "/" too (pen Q66: only Enter sends); editing
                            // goes on. A second Escape leaves the text as
                            // written: what was typed is kept (undo covers it),
                            // never reverted.
                            if (slashOpen && onSlashTrigger !== undefined) {
                                onSlashTrigger("dismissed", slashQuery(draft));
                                return;
                            }
                            commit();
                            return;
                        }
                        // Backspace on an EMPTY block ⇒ delete it + focus previous
                        // (merge of text is deferred). A non-empty block edits text.
                        if (
                            event.key === "Backspace" &&
                            onBackspaceEmpty !== undefined &&
                            draft === ""
                        ) {
                            event.preventDefault();
                            doneRef.current = true;
                            setEditing(false);
                            onBackspaceEmpty();
                            return;
                        }
                    }}
                />
                {slashTriggerState === "open" && draft === "/" ? (
                    // After "/" the caret stays on the paper and the hint
                    // follows it on the same line (pen "Writing").
                    <span
                        className={`${cls} bilda-editable-text__slash-hint`}
                        aria-hidden="true"
                    >
                        <span className="bilda-editable-text__slash-mark">
                            /
                        </span>{" "}
                        {t`Type to filter`}
                    </span>
                ) : null}
                {nearCeiling ? (
                    <p className="bilda-editable-text__limit" role="status">
                        {t`This can be up to ${maxLength} characters`}
                    </p>
                ) : null}
            </>
        );

    // Empty value + a placeholder ⇒ show the faint placeholder in place, as the
    // clickable target (the WORDS say it is empty; the tone is redundant).
    const showPlaceholder = value === "" && placeholder !== undefined;
    return (
        // A native button: keyboard-operable for free (Enter/Space), named by
        // `ariaLabel`, styled (see .css) to read as the plain text it edits.
        <button
            type="button"
            className={`${cls} bilda-editable-text--display${
                showPlaceholder ? " bilda-editable-text--placeholder" : ""
            }`}
            aria-label={ariaLabel}
            onClick={(event) => {
                if (onActivate !== undefined) return onActivate();
                // A keyboard click (Enter/Space) has no point: the end.
                enter(
                    event.detail === 0
                        ? value.length
                        : caretOffsetAt(
                              event.currentTarget,
                              event.clientX,
                              event.clientY,
                              value,
                          ),
                );
            }}
        >
            {showPlaceholder
                ? placeholder
                : renderDisplay
                  ? renderDisplay(value)
                  : value}
        </button>
    );
};
